<script lang="ts" setup>
import RowItem from '@/molecules/RowItem.vue';
import { computed, ref, watch } from 'vue';
import BaseLabel from '@/atoms/BaseLabel.vue';
import BaseCheckbox from '@/atoms/BaseCheckbox.vue';

type ConfigItem = {
  key: string;
  label: string;
  suffix?: string;
  type?: 'boolean' | 'boolean-string' | 'tri-boolean' | 'date' | 'time';
  source?: 'request' | 'flowData';
  showIf?: (data: any) => boolean;
  value?: any;
};

type ConfigSection = {
  section?: string;
  items: ConfigItem[];
};

type ConfigCheckbox = {
  checkbox: true;
  label: string;
  errorMessage?: string;
};

type Config = Array<ConfigItem | ConfigSection | ConfigCheckbox>;

const props = defineProps<{
  data: Record<string, any>;
  config: Config;
}>();

const emit = defineEmits<{
  (e: 'canSubmit', value: boolean): void;
}>();

const isSection = (item: any): item is ConfigSection => item.items !== undefined;
const isCheckbox = (item: any): item is ConfigCheckbox => item.checkbox === true;

const checkboxStates = ref<Record<string, boolean>>({});
const checkboxTouched = ref<Record<string, boolean>>({});

const checkboxItems = computed(() => props.config.filter(isCheckbox) as ConfigCheckbox[]);

const canSubmit = computed(() => checkboxItems.value.every((cb) => checkboxStates.value[cb.label]));

watch(canSubmit, (val) => emit('canSubmit', val), { immediate: true });

const resolveValue = (item: ConfigItem) => {
  if (item?.value !== undefined) return item.value;

  if (item?.source === 'flowData') {
    const fromFlowData = item.key
      .split('.')
      .reduce((acc: any, k: string) => acc?.[k], props.data?.flowData);
    if (fromFlowData !== undefined) return fromFlowData;
    return item.key
      .split('.')
      .reduce((acc: any, k: string) => acc?.[k], props.data?.flowData?.meta);
  }

  return item?.key?.split('.').reduce((acc: any, k: string) => acc?.[k], props.data?.request);
};

const shouldShow = (item: ConfigItem) => {
  if (!item.showIf) return true;
  try {
    return item.showIf(props.data?.request);
  } catch {
    return true;
  }
};

const normalizedConfig = computed(() => {
  return props.config
    .map((item) => {
      if (isSection(item)) {
        return { ...item, items: item.items.filter(shouldShow) };
      }
      return item;
    })
    .filter((item) => {
      if (isSection(item)) return item.items.length > 0;
      return true;
    });
});

const formatValue = (item: ConfigItem, value: any) => {
  if (item.type === 'boolean') return value ? 'Oui' : 'Non';

  if (item.type === 'boolean-string') {
    if (value === 'true') return 'Oui';
    if (value === 'false') return 'Non';
    return 'Je ne sais pas';
  }

  if (item.type === 'tri-boolean') {
    if (value === 'YES') return 'Oui';
    if (value === 'NO') return 'Non';
    return 'Je ne sais pas';
  }

  if (item.type === 'date') {
    if (!value) return '-';
    const d = new Date(String(value).replace(' ', 'T'));
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('fr-FR');
  }

  if (item.type === 'time') {
    if (!value) return '-';
    const d = new Date(String(value).replace(' ', 'T'));
    return isNaN(d.getTime())
      ? '-'
      : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }

  if (item.suffix) return `${value ?? '-'} ${item.suffix}`;

  return value ?? '-';
};
</script>

<template>
  <div>
    <template
      v-for="item in normalizedConfig"
      :key="isSection(item) ? item.section : isCheckbox(item) ? item.label : item.key"
    >
      <!-- ✅ SECTION -->
      <div v-if="isSection(item)">
        <div v-if="item.section" class="pt-6 pb-1">
          <BaseLabel :text="item.section" color="primary" preset="h3" weight="medium" />
        </div>
        <RowItem
          v-for="subItem in item.items"
          :id="subItem.key"
          :key="subItem.key"
          :bordered="false"
          :description="formatValue(subItem, resolveValue(subItem))"
          :title="subItem.label"
          :titleWrap="true"
          class="px-0"
        />
      </div>

      <!-- ✅ CHECKBOX -->
      <div v-else-if="isCheckbox(item)" class="px-4 pt-4">
        <BaseCheckbox
          :id="item.label"
          v-model="checkboxStates[item.label]"
          :title="item.label"
          color-mode="on-brand"
          @update:model-value="checkboxTouched[item.label] = true"
        />
        <p
          v-if="checkboxTouched[item.label] && !checkboxStates[item.label]"
          class="text-red-500 text-xs mt-1 ml-7"
        >
          {{ item.errorMessage ?? 'Ce champ est obligatoire' }}
        </p>
      </div>

      <!-- ✅ ITEM SIMPLE -->
      <RowItem
        v-else
        :id="item.key"
        :key="item.key"
        :bordered="false"
        :description="formatValue(item, resolveValue(item))"
        :title="item.label"
        :titleWrap="true"
        class="px-0"
      />
    </template>
  </div>
</template>
